import type {
  AllMetricKeys,
  FacebookMetrics,
  FacebookVariant,
  GoogleBusinessProfileMetrics,
  InstagramMetrics,
  InstagramVariant,
  LinkedInMetrics,
  MetricLabels,
  PinterestMetrics,
  SocialPlatformMetrics,
  ThreadsMetrics,
  TikTokMetrics,
  TwitterMetrics,
  YouTubeMetrics,
} from '@Components/social-media/social-platform-metrics.types';
import {
  getMetricIconByKey,
  getMetricIconTooltipByKey,
  getReadableMetricByKey,
  getYouTubeTooltipByKey,
  metricsAggregate,
  metricsBase,
  metricsFacebook,
  metricsFacebookStory,
  metricsGoogleBusinessProfile,
  metricsInstagramPost,
  metricsInstagramStory,
  metricsInstagramVideo,
  metricsLinkedInPage,
  metricsPinterest,
  metricsThreads,
  metricsTikTok,
  metricsTwitter,
  metricsYouTube,
} from '@Components/social-media/social-platform-metrics.types';
import type {SocialPost} from '@Components/social-media/post.vo';
import {PostState} from '@Components/social-media/post.vo';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import type {MetricMeta} from '@Components/social-media/components/social-post-dialog-metrics-grid/social-post-dialog-metrics-grid';

interface DelayedMetricsConfig {
  keys: ReadonlySet<AllMetricKeys>;
  delayWindowMs: number;
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Per-platform "delayed metric" config: within `delayWindowMs` of publish, a zero on
 * any listed key is treated as "still processing" (rendered as `PENDING_METRIC_PLACEHOLDER`)
 * rather than a real zero next to populated cells.
 */
export const DELAYED_METRICS_CONFIG_BY_PLATFORM: Partial<Record<SocialPlatform, DelayedMetricsConfig>> = {
  [SocialPlatform.YOUTUBE]: {
    keys: new Set<AllMetricKeys>([
      'numShares',
      'numFollowsAttributed',
      'numSaves',
      'numAverageViewPercentage',
      'numAverageViewDuration',
      'numEstimatedMinutesWatched',
    ]),
    delayWindowMs: 2 * ONE_DAY_MS,
  },
};

export const PENDING_METRIC_PLACEHOLDER = '—';

export const isMetricPending = (platform: SocialPlatform, metricKey: AllMetricKeys, value: number, publishOnUnix: number | undefined): boolean => {
  if (value !== 0 || publishOnUnix === undefined) {
    return false;
  }

  const config = DELAYED_METRICS_CONFIG_BY_PLATFORM[platform];
  if (!config?.keys.has(metricKey)) {
    return false;
  }

  const elapsedMs = Date.now() - publishOnUnix * 1000;
  return elapsedMs >= 0 && elapsedMs < config.delayWindowMs;
};

export const getBaseMetricIconByIndex = (metricIndex: number): string => {
  const baseMetricKey = metricsAggregate[metricIndex] as keyof MetricLabels;
  return getMetricIconByKey(baseMetricKey);
};

const getFacebookKeysForVariant = (variant: FacebookVariant): (keyof FacebookMetrics)[] => {
  return variant === 'story' ? metricsFacebookStory : metricsFacebook;
};

export const getFacebookMetricsKey = (variant: FacebookVariant, index: number): keyof FacebookMetrics => {
  return getFacebookKeysForVariant(variant)[index];
};

export const getFacebookMetricIconByIndex = (metricIndex: number, variant: FacebookVariant): string => {
  const keys = getFacebookKeysForVariant(variant);
  return getMetricIconByKey(keys[metricIndex] as keyof MetricLabels);
};

export const getLinkedinMetricIconByIndex = (metricIndex: number): string => {
  const linkedinMetricKey = metricsLinkedInPage[metricIndex] as keyof MetricLabels;
  return getMetricIconByKey(linkedinMetricKey);
};

export const getTwitterMetricIconByIndex = (metricIndex: number): string => {
  const twitterMetricKey = metricsBase[metricIndex] as keyof MetricLabels;
  return getMetricIconByKey(twitterMetricKey);
};

export const getTikTokMetricIconByIndex = (metricIndex: number): string => {
  const tiktokMetricKey = metricsTikTok[metricIndex] as keyof MetricLabels;
  return getMetricIconByKey(tiktokMetricKey);
};

export const getThreadsMetricIconByIndex = (metricIndex: number): string => {
  const threadsMetricKey = metricsThreads[metricIndex] as keyof MetricLabels;
  return getMetricIconByKey(threadsMetricKey);
};

export const getGoogleBusinessProfileMetricIconByIndex = (metricIndex: number): string => {
  const gbpMetricKey = metricsGoogleBusinessProfile[metricIndex] as keyof MetricLabels;
  return getMetricIconByKey(gbpMetricKey);
};

export const getPinterestMetricIconByIndex = (metricIndex: number): string => {
  const pinterestMetricKey = metricsPinterest[metricIndex] as keyof MetricLabels;
  return getMetricIconByKey(pinterestMetricKey);
};

export const getYouTubeMetricIconByIndex = (metricIndex: number): string => {
  const youtubeMetricKey = metricsYouTube[metricIndex] as keyof MetricLabels;
  return getMetricIconByKey(youtubeMetricKey);
};

const getInstagramKeysForVariant = (variant: InstagramVariant): (keyof InstagramMetrics)[] => {
  switch (variant) {
    case 'story':
      return metricsInstagramStory;
    case 'reel':
      return metricsInstagramVideo;
    case 'image':
    default:
      return metricsInstagramPost;
  }
};

export const getInstagramMetricIconByIndex = (metricIndex: number, variant: InstagramVariant): string => {
  const keys = getInstagramKeysForVariant(variant);
  return getMetricIconByKey(keys[metricIndex] as keyof MetricLabels);
};

export const getFacebookMetricsByIndex = (platformMetrics: SocialPlatformMetrics, index: number, variant: FacebookVariant): number => {
  const metricKey = getFacebookMetricsKey(variant, index);

  if (metricKey === undefined) {
    return 0;
  }

  const facebookMetrics = platformMetrics as FacebookMetrics;
  return facebookMetrics[metricKey] ?? 0;
};

export const getLinkedinMetricsByIndex = (platformMetrics: SocialPlatformMetrics, index: number): number => {
  const metricKey = metricsLinkedInPage[index];
  const linkedinMetrics = platformMetrics as LinkedInMetrics;
  return linkedinMetrics[metricKey] ?? 0;
};

export const getTwitterMetricsByIndex = (platformMetrics: SocialPlatformMetrics, index: number): number => {
  const metricKey = metricsTwitter[index];
  const twitterMetrics = platformMetrics as TwitterMetrics;
  return twitterMetrics[metricKey] ?? 0;
};

export const getPinterestMetricsByIndex = (platformMetrics: SocialPlatformMetrics, index: number): number => {
  const metricKey = metricsPinterest[index];
  const pinterestMetrics = platformMetrics as PinterestMetrics;
  return pinterestMetrics[metricKey] ?? 0;
};

export const getTikTokMetricsByIndex = (platformMetrics: SocialPlatformMetrics, index: number): number => {
  const metricKey = metricsTikTok[index];
  const tiktokMetrics = platformMetrics as TikTokMetrics;
  return tiktokMetrics[metricKey] ?? 0;
};

export const getThreadsMetricsKey = (index: number): keyof ThreadsMetrics => {
  return metricsThreads[index];
};

export const getThreadsMetricsByIndex = (platformMetrics: SocialPlatformMetrics, index: number): number => {
  const metricKey = metricsThreads[index];
  const threadsMetrics = platformMetrics as ThreadsMetrics;
  return threadsMetrics[metricKey] ?? 0;
};

export const getGoogleBusinessProfileMetricsByIndex = (platformMetrics: SocialPlatformMetrics, index: number): number => {
  const metricKey = metricsGoogleBusinessProfile[index];
  const gbpMetrics = platformMetrics as GoogleBusinessProfileMetrics;
  return gbpMetrics[metricKey] ?? 0;
};

export const getYouTubeMetricsByIndex = (platformMetrics: SocialPlatformMetrics, index: number): number => {
  const metricKey = metricsYouTube[index];
  const youtubeMetrics = platformMetrics as YouTubeMetrics;
  return youtubeMetrics[metricKey] ?? 0;
};

export const getInstagramMetricsByIndex = (platformMetrics: SocialPlatformMetrics, index: number, variant: InstagramVariant): number => {
  const metricKey = getInstagramMetricsKey(variant, index);

  if (metricKey === undefined) {
    return 0;
  }

  const instagramMetrics = platformMetrics as InstagramMetrics;
  return instagramMetrics[metricKey] ?? 0;
};

export const getInstagramMetricsKey = (variant: InstagramVariant, index: number): keyof InstagramMetrics => {
  return getInstagramKeysForVariant(variant)[index];
};

export const getBaseMetricDescriptionByIndex = (metricIndex: number): string => {
  const baseMetricKey = metricsAggregate[metricIndex] as keyof MetricLabels;
  return getReadableMetricByKey(baseMetricKey, false);
};

export const getFacebookMetricDescriptionByIndex = (metricIndex: number, variant: FacebookVariant): string => {
  const keys = getFacebookKeysForVariant(variant);
  return getReadableMetricByKey(keys[metricIndex] as keyof MetricLabels, true);
};

export const getLinkedInMetricDescriptionByIndex = (metricIndex: number): string => {
  const linkedinMetricKey = metricsLinkedInPage[metricIndex] as keyof MetricLabels;
  return getReadableMetricByKey(linkedinMetricKey, false);
};

export const getTwitterMetricDescriptionByIndex = (metricIndex: number): string => {
  const twitterMetricKey = metricsTwitter[metricIndex] as keyof MetricLabels;
  return getReadableMetricByKey(twitterMetricKey, false);
};

export const getYouTubeMetricDescriptionByIndex = (metricIndex: number): string => {
  const youtubeMetricKey = metricsYouTube[metricIndex] as keyof MetricLabels;
  return getReadableMetricByKey(youtubeMetricKey, false);
};

export const getPinterestMetricDescriptionByIndex = (metricIndex: number): string => {
  const pinterestMetricKey = metricsPinterest[metricIndex] as keyof MetricLabels;
  return getReadableMetricByKey(pinterestMetricKey, false);
};

export const getTikTokMetricDescriptionByIndex = (metricIndex: number): string => {
  const tiktokMetricKey = metricsTikTok[metricIndex] as keyof MetricLabels;
  return getReadableMetricByKey(tiktokMetricKey, false);
};

export const getThreadsMetricDescriptionByIndex = (metricIndex: number): string => {
  const threadsMetricKey = metricsThreads[metricIndex] as keyof MetricLabels;
  return getReadableMetricByKey(threadsMetricKey, false);
};

export const getGoogleBusinessProfileMetricDescriptionByIndex = (metricIndex: number): string => {
  const gbpMetricKey = metricsGoogleBusinessProfile[metricIndex] as keyof MetricLabels;
  return getReadableMetricByKey(gbpMetricKey, false);
};

export const getInstagramDescriptionByIndex = (metricIndex: number, variant: InstagramVariant): string => {
  const keys = getInstagramKeysForVariant(variant);
  return getReadableMetricByKey(keys[metricIndex] as keyof MetricLabels, false);
};

export const getBaseIconTooltipByIndex = (metricIndex: number): string => {
  const baseMetricKey = metricsAggregate[metricIndex] as keyof MetricLabels;
  return getMetricIconTooltipByKey(baseMetricKey, true, false);
};

export const getFacebookIconTooltipByIndex = (metricIndex: number, variant: FacebookVariant): string => {
  const keys = getFacebookKeysForVariant(variant);
  return getMetricIconTooltipByKey(keys[metricIndex] as keyof MetricLabels, false, true);
};

export const getInstagramIconTooltipByIndex = (metricIndex: number, variant: InstagramVariant): string => {
  const keys = getInstagramKeysForVariant(variant);
  return getMetricIconTooltipByKey(keys[metricIndex] as keyof MetricLabels, false, false);
};

export const getLinkedInIconTooltipByIndex = (metricIndex: number): string => {
  const linkedinMetricKey = metricsLinkedInPage[metricIndex] as keyof MetricLabels;
  return getMetricIconTooltipByKey(linkedinMetricKey, false, false);
};

export const getYouTubeIconTooltipByIndex = (metricIndex: number): string => {
  const youtubeMetricKey = metricsYouTube[metricIndex] as keyof MetricLabels;
  return getYouTubeTooltipByKey(youtubeMetricKey);
};

export const getPinterestIconTooltipByIndex = (metricIndex: number): string => {
  const pinterestMetricKey = metricsPinterest[metricIndex] as keyof MetricLabels;
  return getMetricIconTooltipByKey(pinterestMetricKey, false, false);
};

export const getTikTokIconTooltipByIndex = (metricIndex: number): string => {
  const tiktokMetricKey = metricsTikTok[metricIndex] as keyof MetricLabels;
  return getMetricIconTooltipByKey(tiktokMetricKey, false, false);
};

export const getThreadsIconTooltipByIndex = (metricIndex: number): string => {
  const threadsMetricKey = metricsThreads[metricIndex] as keyof MetricLabels;
  return getMetricIconTooltipByKey(threadsMetricKey, false, false);
};

export const getGoogleBusinessProfileIconTooltipByIndex = (metricIndex: number): string => {
  const gbpMetricKey = metricsGoogleBusinessProfile[metricIndex] as keyof MetricLabels;
  return getMetricIconTooltipByKey(gbpMetricKey, false, false);
};

export const getTwitterIconTooltipByIndex = (metricIndex: number): string => {
  const twitterMetricKey = metricsTwitter[metricIndex] as keyof MetricLabels;
  return getMetricIconTooltipByKey(twitterMetricKey, false, false);
};

export const truncateDecimalsIfNeeded = (value: string): string => {
  if (value.endsWith('.00')) {
    return `${value.slice(0, -3)}`;
  }

  if (value.endsWith('0')) {
    return `${value.slice(0, -1)}`;
  }

  return value;
};

const isNumberADecimal = (value: number): boolean => {
  return value - Math.floor(value) !== 0;
};

/**
 * Watch-time keys are stored as integer seconds (or minutes for YouTube's
 * estimatedMinutesWatched) but rendered as human-readable durations. Anything
 * here goes through {@link getReadableWatchTime}; everything else uses the
 * default K/M number compaction.
 */
const WATCH_TIME_SECONDS_KEYS = new Set<AllMetricKeys>(['avgWatchTimeSeconds', 'totalWatchTimeSeconds', 'numAverageViewDuration']);
const WATCH_TIME_MINUTES_KEYS = new Set<AllMetricKeys>(['numEstimatedMinutesWatched']);
const PERCENTAGE_KEYS = new Set<AllMetricKeys>(['numAverageViewPercentage']);

const getReadableWatchTime = (totalSeconds: number): string => {
  if (totalSeconds <= 0) {
    return '0s';
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  }

  if (minutes > 0) {
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }

  return `${seconds}s`;
};

export const getReadableMetrics = (metricMeta: MetricMeta): string => {
  const {value, type} = metricMeta;

  if (type === 'numEngagement') {
    const percentageValue = Math.ceil(value * 100);
    return `${percentageValue}%`;
  }

  if (type !== undefined && PERCENTAGE_KEYS.has(type)) {
    return `${Math.round(value)}%`;
  }

  if (type !== undefined && WATCH_TIME_SECONDS_KEYS.has(type)) {
    return getReadableWatchTime(value);
  }

  if (type !== undefined && WATCH_TIME_MINUTES_KEYS.has(type)) {
    return getReadableWatchTime(value * 60);
  }

  if (value >= 1e6) {
    const formattedValue = (value / 1e6).toFixed(2);
    return `${truncateDecimalsIfNeeded(formattedValue)}M`;
  }

  if (value >= 1e3) {
    const formattedValue = (value / 1e3).toFixed(2);
    return `${truncateDecimalsIfNeeded(formattedValue)}K`;
  }

  if (isNumberADecimal(value)) {
    return `${value.toFixed(2)}`;
  }

  return value.toString();
};

export const getTooltipReadableMetrics = (value: number): string => {
  return value.toLocaleString('en-US');
};

export const getNumErrorAccounts = (post: SocialPost): number => {
  let count = 0;

  if (post.state === PostState.PUBLISHED && post.publishingParams) {
    const accountIds = Object.keys(post.publishingParams);

    accountIds.forEach((accountId): void => {
      const postPublishingParams = post.publishingParams[accountId].POST;
      const storyPublishingParams = post.publishingParams[accountId].STORIES;
      const reelPublishingParams = post.publishingParams[accountId].REELS;

      if (!postPublishingParams && !storyPublishingParams && !reelPublishingParams) {
        console.error('No Publishing Params found for any MediaType');
        return;
      }

      if (postPublishingParams?.state === PostState.ERROR || storyPublishingParams?.state === PostState.ERROR || reelPublishingParams?.state === PostState.ERROR) {
        count += 1;
      }
    });
  }

  return count;
};
